import _get from 'lodash.get';
import { ScannedDokument } from '../components/visum-erfassung/dokumente/scanned.document';
import { AppConfig } from '../models/config/app-config';
import { VisumAntrag } from '../models/visum.antrag';
import { VisumDokument } from '../models/visum.dokument';
import { DateFormatter } from './date-utils.service';
import { Marshaller } from './marshaller';
import { ValueListsService } from './value-lists.service';

export class LangfristigerAufenthaltMarshaller extends Marshaller {

  constructor(
    valueListsService: ValueListsService,
    dateFormatter: DateFormatter,
    private appConfig: AppConfig
  ) {
    super(valueListsService, dateFormatter);
  }

  public marshall(formValue: any): VisumAntrag {
    const ret: any = this.marshallAlphanumericOnly(formValue);

    if (formValue.lichtbild || formValue.fingerabdruck) {
      ret.antragsteller.biometrie = {};
    }

    if (formValue.lichtbild) {
      if (formValue.lichtbild.lichtbild) {
        ret.antragsteller.biometrie.lichtbild = formValue.lichtbild.lichtbild;
      }
    }

    if (formValue.fingerabdruck) {
      if (formValue.fingerabdruck.fingerabdruecke) {
        ret.antragsteller.biometrie.fingerabdruecke = formValue.fingerabdruck.fingerabdruecke;
      }

      ret.antragsteller.biometrie.fingerabdrueckeAusnahmegrund = formValue.fingerabdruck.ausnahmegrund;
    }

    if (formValue.dokumente) {
      const dokumentListe: any[] = [];

      const dokumentListFromForm: ScannedDokument[] = formValue.dokumente.pdfDocuments;
      if (dokumentListFromForm) {
        dokumentListFromForm.forEach((scannedDokumentValue: ScannedDokument) => {
          dokumentListe.push({
            art: this.valueListSelection('dokument_art', scannedDokumentValue.doctype),
            dokument: scannedDokumentValue.data,
            groesseInBytes: scannedDokumentValue.sizeInBytes,
            digitialisierungsdatum: scannedDokumentValue.creationDateAndTime
          });
        });
      }

      if (dokumentListe.length > 0) {
        ret.dokumentListe = dokumentListe;
      }
    }

    return ret;
  }

  public marshallAlphanumericOnly(formValue: any) {
    const ret: any = {
      visumAntragsart: this.appConfig.visumAntragsartEnum,

      visumdaten: {
        gueltigkeit: {
          von: this.getFormattedDatePickerValue(formValue.reisedaten.von),

          // Für "Aufenthalt in Deutschland - Bis" gibt es zwei Felder, von denen immer nur eines angezeigt wird:
          // Falls die "beabsichtigte Aufenthaltsdauer kleiner 12 Monate" ist (keine12MonateAufenthalt == true),
          // wird ein Datumsfeld mit Datepicker angezeigt (bisGenau) andernfalls ein Textfeld (bisUngefaehr).
          // Wegen der unterschiedlichen Validierung gibt es dafür auch im Domain-Model zwei verschiedene Attribute.
          bisGenau: formValue.reisedaten.keine12MonateAufenthalt
            ? { value: this.getFormattedDatePickerValue(formValue.reisedaten.bisGenau) }
            : undefined,
          bisUngefaehr: formValue.reisedaten.keine12MonateAufenthalt
            ? undefined
            : { value: this.get(formValue.reisedaten.bisUngefaehr) }
        }
      },

      antragsteller: {
        familienname: this.get(formValue.angabenZurPerson.familienname),
        vorname: this.get(formValue.angabenZurPerson.vorname),
        geburtsname: this.get(formValue.angabenZurPerson.geburtsname),
        geburtsland: this.valueListSelection('land', formValue.angabenZurPerson.geburtsland),
        geburtsort: this.get(formValue.angabenZurPerson.geburtsort),
        geburtsdatum: this.get(formValue.angabenZurPerson.geburtsdatum),
        staatsangehoerigkeitBeiGeburtListe: this.valueListArraySelection(
          'land',
          formValue.angabenZurPerson.staatsangehoerigkeitBeiGeburt1,
          formValue.angabenZurPerson.staatsangehoerigkeitBeiGeburt2,
          formValue.angabenZurPerson.staatsangehoerigkeitBeiGeburt3
        ),
        staatsangehoerigkeitListe: this.valueListArraySelection(
          'land',
          formValue.angabenZurPerson.derzeitigeStaatsangehoerigkeit1,
          formValue.angabenZurPerson.derzeitigeStaatsangehoerigkeit2,
          formValue.angabenZurPerson.derzeitigeStaatsangehoerigkeit3
        ),
        geschlecht: this.valueListSelection('geschlecht', formValue.angabenZurPerson.geschlecht),
        familienstand: this.valueListSelection('familienstand', formValue.angabenZurPerson.familienstand),

        // "familienstandSeit" ist ein Pflichtfeld, wird aber nur angezeigt, wenn "familienstand" weder 'ledig' noch
        // 'unbekannt' ist. Für die Validierung wurde im Backend eine Wrapper-Klasse eingeführt, deren einziges
        // Attribut "value" als Pflichtfeld markiert wurde. Das Attribut "familienstandUngleichLedigUndUnbekanntSeit"
        // das eine Instanz dieser Wrapper-Klasse hält, darf aber null sein!
        familienstandUngleichLedigUndUnbekanntSeit: ([
          'unbekannt',
          'ledig'
        ].indexOf(formValue.angabenZurPerson.familienstand) < 0)
          ? { value: this.get(formValue.angabenZurPerson.familienstandSeit) }
          : undefined,

        pass: {
          passnummer: this.get(formValue.ausweispapiere.passNummer),
          passArt: this.valueListSelection('pass_art', formValue.ausweispapiere.passArt),
          ausgestelltVon: this.get(formValue.ausweispapiere.passAusgestelltvon),
          ausstellenderStaat: this.valueListSelection('land', formValue.ausweispapiere.passAusstellerstaat),
          gueltigVon: this.getFormattedDatePickerValue(formValue.ausweispapiere.passAusstellungsdatum),
          gueltigBis: this.getFormattedDatePickerValue(formValue.ausweispapiere.passGueltigbis)
        },

        personendaten: {
          abweichendeSchreibweiseName: this.get(formValue.zusaetzlicheAngaben)
            ? this.get(formValue.zusaetzlicheAngaben.abweichendeSchreibweiseName)
            : undefined,
          wohnortImAusland: (formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.wohnortImAusland === true),
          wohnortImAuslandOrt: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.wohnortImAuslandOrt),
          mitreisendeFamilienangehoerige: (formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.mitreisendeFamilienangehoerige === true),
          mitreisendeFamilienangehoerigeText: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.mitreisendeFamilienangehoerigeText),
          krankenversicherung: (formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.krankenversicherung === true),
          berufdaten: {
            berufAuswahl: this.valueListSelection('beruf', formValue.angabenZurPerson.berufAuswahl),
            berufSonstiges: this.get(formValue.angabenZurPerson.erlernterBerufSonstiges),
            derzeitigeBeruflicheTaetigkeit: this.get(formValue.angabenZurPerson.derzeitigerBeruf),
            derzeitigeBeruflicheTaetigkeitSonstiges: this.get(formValue.angabenZurPerson.derzeitigerBerufSonstiges)
          },
          staendigeAnschrift: {
            // TODO: "beibehalten: true" sollte im Backend gesetzt/vorbelegt werden!
            beibehalten: true,
            land: this.valueListSelection('land', formValue.kontaktdaten.anschriftLand),
            ort: this.get(formValue.kontaktdaten.anschriftOrt),
            plz: this.get(formValue.kontaktdaten.anschriftPLZ),
            strasse: this.get(formValue.kontaktdaten.anschriftStrasse),
            hausnummer: this.get(formValue.kontaktdaten.anschriftHausnummer),
            sonstigeAdressangaben: this.get(formValue.kontaktdaten.anschriftSonstiges),
            kontaktdaten: {
              email: this.get(formValue.kontaktdaten.anschriftEmail),
              telefon: this.get(formValue.kontaktdaten.anschriftTelefon)
            }
          },
          eltern: {
            vater: {
              staatsangehoerigkeitListe: this.valueListArraySelection(
                'land',
                formValue.angabenZurPerson.staatsangehoerigkeitVater
              ),
              familienname: this.get(formValue.angabenZurPerson.familiennameVater),
              vorname: this.get(formValue.angabenZurPerson.vornameVater),
              geburtsdatum: this.get(formValue.angabenZurPerson.geburtsdatumVater),
              geburtsort: this.get(formValue.angabenZurPerson.geburtsortVater),
              wohnort: this.get(formValue.angabenZurPerson.wohnortVater)
            },
            mutter: {
              staatsangehoerigkeitListe: this.valueListArraySelection(
                'land',
                formValue.angabenZurPerson.staatsangehoerigkeitMutter
              ),
              familienname: this.get(formValue.angabenZurPerson.familiennameMutter),
              vorname: this.get(formValue.angabenZurPerson.vornameMutter),
              geburtsdatum: this.get(formValue.angabenZurPerson.geburtsdatumMutter),
              geburtsort: this.get(formValue.angabenZurPerson.geburtsortMutter),
              wohnort: this.get(formValue.angabenZurPerson.wohnortMutter)
            }
          },

          // Der Abschnitt zum Ehegatten wird nur angezeigt, wenn "familienstand" einen der folgenden Werte hat:
          ehegatte: ([
            'verheiratet',
            'getrennt lebend',
            'eingetr. Partnerschaft'
          ].indexOf(formValue.angabenZurPerson.familienstand) >= 0)
            ? {
              familienname: this.get(formValue.angabenZurPerson.ehegatteFamilienname),
              geburtsname: this.get(formValue.angabenZurPerson.ehegatteGeburtsname),
              vorname: this.get(formValue.angabenZurPerson.ehegatteVorname),
              geburtsdatum: this.get(formValue.angabenZurPerson.ehegatteGeburtsdatum),
              geburtsort: this.get(formValue.angabenZurPerson.ehegatteGeburtsort),
              geburtsland: this.valueListSelection('land', formValue.angabenZurPerson.ehegatteGeburtsland),
              staatsangehoerigkeitListe: this.valueListArraySelection(
                'land',
                formValue.angabenZurPerson.ehegatteDerzeitigeStaatsangehoerigkeit1,
                formValue.angabenZurPerson.ehegatteDerzeitigeStaatsangehoerigkeit2,
                formValue.angabenZurPerson.ehegatteDerzeitigeStaatsangehoerigkeit3
              ),
              staatsangehoerigkeitBeiGeburtListe: this.valueListArraySelection(
                'land',
                formValue.angabenZurPerson.ehegatteStaatsangehoerigkeitBeiGeburt1,
                formValue.angabenZurPerson.ehegatteStaatsangehoerigkeitBeiGeburt2,
                formValue.angabenZurPerson.ehegatteStaatsangehoerigkeitBeiGeburt3
              ),
              wohnort: this.get(formValue.angabenZurPerson.ehegatteWohnort)
            }
            : undefined,

          kinder: {
            kindListe: formValue.angabenZurPerson.kinder ? formValue.angabenZurPerson.kinder
            .filter(kind => !!kind)  // falsy-Values herausfilern (undefined, null, etc.)
            .map(kind => {
              return {
                familienname: this.get(kind.familienname),
                vorname: this.get(kind.vorname),
                geschlecht: this.valueListSelection('geschlecht', kind.geschlecht),
                geburtsdatum: this.get(kind.geburtsdatum),
                geburtsort: this.get(kind.geburtsort),
                wohnort: this.get(kind.wohnort),
                staatsangehoerigkeitListe: this.valueListArraySelection(
                  'land',
                  kind.staatsangehoerigkeit1,
                  kind.staatsangehoerigkeit2,
                  kind.staatsangehoerigkeit3
                )
              };
            }) : []
          }
        },

        aufenthaltsberechtigung: formValue.kontaktdaten.wohnsitzAnderesLand === true
          ? {
            artDerRueckkehrberechtigung: this.get(formValue.kontaktdaten.aufenthaltsberechtigungArt),
            rueckkehrDokumentNr: this.get(formValue.kontaktdaten.aufenthaltsberechtigungNummer),
            rueckkehrGueltigBis: this.getFormattedDatePickerValue(formValue.kontaktdaten.aufenthaltsberechtigungGueltigkeitbis)
          }
          : undefined
      },

      reisedaten: {
        keine12MonateAufenthalt: (formValue.reisedaten.keine12MonateAufenthalt === true),
        dvisumAufenthaltszweck: this.valueListSelection('d_visum_aufenthaltszweck', formValue.reisedaten.dVisumZweck),
        // "dvisumAufenthaltszweckSonstiges"  wird nur angezeigt, wenn "dvisumAufenthaltszweck" 'Sonstiges'
        dvisumAufenthaltszweckSonstiges: formValue.reisedaten.dVisumZweck === 'Sonstiges'
          ? this.get(formValue.reisedaten.dVisumZweckSonstiges)
          : undefined,

        dvisumErwerbstaetigkeit: this.get(formValue.reisedaten.dVisumErwerbstaetigkeit),
        // aufenthaltszweckListe: this.valueListArraySelection('aufenthaltszweck', formValue.reisedaten.cVisumZweck),
        befoerderungsmittel: this.get(formValue.zusaetzlicheAngaben)
          ? this.valueListSelection('befoerderungsmittel', formValue.zusaetzlicheAngaben.befoerderungsmittel)
          : undefined,

        lebensunterhalt: {
          sonstigesText: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.mittel)
        },
        reisekrankenversicherung: this.get(formValue.zusaetzlicheAngaben)
          ? {
            daten: formValue.zusaetzlicheAngaben.rkvJaNein === true && formValue.zusaetzlicheAngaben.rkvAnbieter
              ? { gesellschaft: this.get(formValue.zusaetzlicheAngaben.rkvAnbieter) }
              : undefined
          }
          : undefined
      },

      referenz: this.marshallReferenz(formValue),

      verpflichtungserklaerungsgeber:
        formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.abweichenderVerpflichtungsgeber === 'jaAbweichend'
          ? {
            art: this.valueListSelection('verpflichtungserklaerungsgeber_art', formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art),

            // organisation nur bei abweichenderVerpflichtungsgeber.art "Oranisation" oder "Diplomat"
            organisation: (['2', '3'].indexOf(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art) >= 0)
              ? {
                name: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.orgaName),
                land: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '2' ? this.valueListSelection('land', formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.orgaSitzLand) : undefined,
                sitz: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '2' ? this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.orgaSitz) : undefined,
                wirkungsbereich: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '2' ? this.valueListSelection('organisation_wirkungsbereich', formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.orgaAufgabe) : undefined,
                registerName: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '2' ? this.valueListSelection('organisation_register', formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.orgaRegisterName) : undefined,
                registerOrt: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '2' ? this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.orgaRegisterOrt) : undefined,
                registerNummer: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '2' ? this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.orgaRegisternummer) : undefined
              }
              : undefined,

            ansprechpartner: {
              staatsangehoerigkeit: this.valueListSelection('land', formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.staatsangehoerigkeit),
              familienname: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.familienname),
              vorname: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.vorname),
              geburtsort: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.geburtsort),
              geburtsdatum: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.geburtsdatum),
              geschlecht: this.valueListSelection('geschlecht', formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.geschlecht),
              anschrift: {
                land: this.valueListSelection('land', formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.land),
                ort: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.ort),
                plz: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.plz),
                strasse: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.strasse),
                hausnummer: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.hausnummer)
              },
              kontaktdaten: {
                email: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.email),
                telefon: this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.telefon)
              },

              // abweichende Schreibweisen und frühere/andere Namen nur bei abweichenderVerpflichtungsgeber.art "Person"
              abweichendeSchreibweiseNachname: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '1'
                ? this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.abweichendNachname)
                : undefined,
              abweichendeSchreibweiseVorname: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '1'
                ? this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.abweichendVorname)
                : undefined,
              fruehereNamen: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '1'
                ? this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.fruehereNamen)
                : undefined,
              andereNamen: formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.art === '1'
                ? this.get(formValue.lebensunterhaltAufenthaltsdetails.abweichenderVerpflichtungsgeber.andereNamen)
                : undefined
            },

            foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue)
          }
          : undefined,

      zusatzangaben: {
        aufenthaltPlz: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.aufenthaltPlz),
        aufenthaltOrt: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.aufenthaltort),
        aufenthaltStrasse: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.aufenthaltStrasse),
        aufenthaltHausnummer: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.aufenthaltHausnummer),
        aufenthaltArt: {
          einzelzimmer: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.einzelzimmer),
          wohnung: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.wohnung),
          zimmer: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.zimmer),
          sammelunterkunft: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.sammelunterkunft),
          sonstiges: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.sonstiges),
          sonstigesText: this.get(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.sonstigesTxt)
        },
        weitereAngaben: this.get(formValue.zusaetzlicheAngaben)
          ? this.get(formValue.zusaetzlicheAngaben.weitereAngaben)
          : undefined
      },

      sonstiges: this.get(formValue.zusaetzlicheAngaben)
        ? {
          abholer: this.get(formValue.zusaetzlicheAngaben.abholer),
          familienname: this.get(formValue.zusaetzlicheAngaben.abholer) === true ? this.get(formValue.zusaetzlicheAngaben.familienname) : undefined,
          vorname: this.get(formValue.zusaetzlicheAngaben.abholer) === true ? this.get(formValue.zusaetzlicheAngaben.vorname) : undefined,
          anschrift: this.get(formValue.zusaetzlicheAngaben.abholer) === true ? this.get(formValue.zusaetzlicheAngaben.anschrift) : undefined
        }
        : undefined,

      fruehereReisen: {
        aufenthalteVorhanden: formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte && (formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.AufenthaltBRDJa === true),
        aufenthaltListe: formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte && formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.AufenthaltBRDJa === true
          ? [{
            ort: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltOrt1),
            von: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltVon1),
            bis: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltBis1)
          },
            {
              ort: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltOrt2),
              von: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltVon2),
              bis: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltBis2)
            },
            {
              ort: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltOrt3),
              von: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltVon3),
              bis: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltBis3)
            },
            {
              ort: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltOrt4),
              von: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltVon4),
              bis: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltBis4)
            },
            {
              ort: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltOrt5),
              von: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltVon5),
              bis: this.get(formValue.lebensunterhaltAufenthaltsdetails.fruehereAufenthalte.aufenthaltBis5)
            }
          ].filter(aufenthalt => {
            return aufenthalt.ort || aufenthalt.von || aufenthalt.bis;
          })
          : undefined
      },

      erklaerung: {
        vorbestraft: (formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftJaNein === 'ja'),
        strafeInDeutschland: formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftJaNein === 'ja'
          ? {
            wannUndWo: this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftDeutschlandWann),
            grund: this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftDeutschlandGrund),
            artUndHoeheDerStrafe: this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftDeutschlandHoehe)
          }
          : undefined,
        strafeImAusland: formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftJaNein === 'ja'
          ? {
            wannUndWo: this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftAuslandWann),
            grund: this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftAuslandGrund),
            artUndHoeheDerStrafe: this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.vorbestraftAuslandHoehe)
          }
          : undefined,

        ausgewiesen: (formValue.lebensunterhaltAufenthaltsdetails.erklaerung.ausgewiesenJaNein === 'ja'),
        ausgewiesenAngaben: formValue.lebensunterhaltAufenthaltsdetails.erklaerung.ausgewiesenJaNein === 'ja'
          ? this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.ausgewiesenGrund)
          : undefined,

        krankheiten: (formValue.lebensunterhaltAufenthaltsdetails.erklaerung.krankJaNein === 'ja'),
        krankheitenAngaben: formValue.lebensunterhaltAufenthaltsdetails.erklaerung.krankJaNein === 'ja'
          ? this.get(formValue.lebensunterhaltAufenthaltsdetails.erklaerung.krankInfos)
          : undefined
      }
    };

    return ret;
  }

  public unmarshall(backendObject: any): any {
    if (this.getAntragsart(backendObject) !== this.appConfig.visumAntragsartEnum) {
      return {};
    }
    const ret = {
      angabenZurPerson: {
        familienname: _get(backendObject, 'antragsteller.familienname', null),
        vorname: _get(backendObject, 'antragsteller.vorname', null),
        geburtsdatum: _get(backendObject, 'antragsteller.geburtsdatum', null),
        geburtsname: _get(backendObject, 'antragsteller.geburtsname', null),
        geburtsort: _get(backendObject, 'antragsteller.geburtsort', null),
        geburtsland: _get(backendObject, 'antragsteller.geburtsland.valueListItemKey', null),
        familienstand: _get(backendObject, 'antragsteller.familienstand.valueListItemKey', null),
        familienstandSeit: _get(backendObject, 'antragsteller.familienstandUngleichLedigUndUnbekanntSeit.value', null),
        geschlecht: _get(backendObject, 'antragsteller.geschlecht.valueListItemKey', null),
        familiennameVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.familienname', null),
        vornameVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.vorname', null),
        staatsangehoerigkeitVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.staatsangehoerigkeitListe[0].valueListItemKey', null),
        geburtsdatumVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.geburtsdatum', null),
        geburtsortVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.geburtsort', null),
        wohnortVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.wohnort', null),
        familiennameMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.familienname', null),
        vornameMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.vorname', null),
        staatsangehoerigkeitMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.staatsangehoerigkeitListe[0].valueListItemKey', null),
        geburtsdatumMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.geburtsdatum', null),
        geburtsortMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.geburtsort', null),
        wohnortMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.wohnort', null),
        berufAuswahl: _get(backendObject, 'antragsteller.personendaten.berufdaten.berufAuswahl.valueListItemKey', null),
        erlernterBerufSonstiges: _get(backendObject, 'antragsteller.personendaten.berufdaten.berufSonstiges', null),
        derzeitigerBeruf: _get(backendObject, 'antragsteller.personendaten.berufdaten.derzeitigeBeruflicheTaetigkeit', null),
        derzeitigerBerufSonstiges: _get(backendObject, 'antragsteller.personendaten.berufdaten.derzeitigeBeruflicheTaetigkeitSonstiges', null),
        derzeitigeStaatsangehoerigkeit1: _get(backendObject, 'antragsteller.staatsangehoerigkeitListe[0].valueListItemKey', null),
        derzeitigeStaatsangehoerigkeit2: _get(backendObject, 'antragsteller.staatsangehoerigkeitListe[1].valueListItemKey', null),
        derzeitigeStaatsangehoerigkeit3: _get(backendObject, 'antragsteller.staatsangehoerigkeitListe[2].valueListItemKey', null),
        staatsangehoerigkeitBeiGeburt1: _get(backendObject, 'antragsteller.staatsangehoerigkeitBeiGeburtListe[0].valueListItemKey', null),
        staatsangehoerigkeitBeiGeburt2: _get(backendObject, 'antragsteller.staatsangehoerigkeitBeiGeburtListe[1].valueListItemKey', null),
        staatsangehoerigkeitBeiGeburt3: _get(backendObject, 'antragsteller.staatsangehoerigkeitBeiGeburtListe[2].valueListItemKey', null),
        ehegatteFamilienname: _get(backendObject, 'antragsteller.personendaten.ehegatte.familienname', null),
        ehegatteGeburtsname: _get(backendObject, 'antragsteller.personendaten.ehegatte.geburtsname', null),
        ehegatteVorname: _get(backendObject, 'antragsteller.personendaten.ehegatte.vorname', null),
        ehegatteGeburtsdatum: _get(backendObject, 'antragsteller.personendaten.ehegatte.geburtsdatum', null),
        ehegatteGeburtsort: _get(backendObject, 'antragsteller.personendaten.ehegatte.geburtsort', null),
        ehegatteGeburtsland: _get(backendObject, 'antragsteller.personendaten.ehegatte.geburtsland.valueListItemKey', null),
        ehegatteWohnort: _get(backendObject, 'antragsteller.personendaten.ehegatte.wohnort', null),
        ehegatteDerzeitigeStaatsangehoerigkeit1: _get(backendObject, 'antragsteller.personendaten.ehegatte.staatsangehoerigkeitListe[0].valueListItemKey', null),
        ehegatteDerzeitigeStaatsangehoerigkeit2: _get(backendObject, 'antragsteller.personendaten.ehegatte.staatsangehoerigkeitListe[1].valueListItemKey', null),
        ehegatteDerzeitigeStaatsangehoerigkeit3: _get(backendObject, 'antragsteller.personendaten.ehegatte.staatsangehoerigkeitListe[2].valueListItemKey', null),
        ehegatteStaatsangehoerigkeitBeiGeburt1: _get(backendObject, 'antragsteller.personendaten.ehegatte.staatsangehoerigkeitBeiGeburtListe[0].valueListItemKey', null),
        ehegatteStaatsangehoerigkeitBeiGeburt2: _get(backendObject, 'antragsteller.personendaten.ehegatte.staatsangehoerigkeitBeiGeburtListe[1].valueListItemKey', null),
        ehegatteStaatsangehoerigkeitBeiGeburt3: _get(backendObject, 'antragsteller.personendaten.ehegatte.staatsangehoerigkeitBeiGeburtListe[2].valueListItemKey', null),
        hasKinder: _get(backendObject, 'antragsteller.personendaten.kinder.kindListe', null) && _get(backendObject, 'antragsteller.personendaten.kinder.kindListe', null).length > 0,
        kinder: _get(backendObject, 'antragsteller.personendaten.kinder.kindListe', null) ? this.decodedKinder2Model(_get(backendObject, 'antragsteller.personendaten.kinder.kindListe', null)) : []
      },
      kontaktdaten: {
        anschriftStrasse: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.strasse', null),
        anschriftHausnummer: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.hausnummer', null),
        anschriftSonstiges: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.sonstigeAdressangaben', null),
        anschriftPLZ: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.plz', null),
        anschriftOrt: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.ort', null),
        anschriftLand: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.land.valueListItemKey', null),
        anschriftTelefon: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.kontaktdaten.telefon', null),
        anschriftEmail: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.kontaktdaten.email', null),
        aufenthaltsberechtigungArt: _get(backendObject, 'antragsteller.aufenthaltsberechtigung.artDerRueckkehrberechtigung', null),
        aufenthaltsberechtigungNummer: _get(backendObject, 'antragsteller.aufenthaltsberechtigung.rueckkehrDokumentNr', null),
        aufenthaltsberechtigungGueltigkeitbis: this.stringAsDatepickerModel(_get(backendObject, 'antragsteller.aufenthaltsberechtigung.rueckkehrGueltigBis', null)),
        wohnsitzAnderesLand: _get(backendObject, 'antragsteller.aufenthaltsberechtigung', false) ? true : false
      },
      ausweispapiere: {
        passArt: _get(backendObject, 'antragsteller.pass.passArt.valueListItemKey', null),
        passNummer: _get(backendObject, 'antragsteller.pass.passnummer', null),
        passAusstellungsdatum: this.stringAsDatepickerModel(_get(backendObject, 'antragsteller.pass.gueltigVon', null)),
        passGueltigbis: this.stringAsDatepickerModel(_get(backendObject, 'antragsteller.pass.gueltigBis', null)),
        passAusstellerstaat: _get(backendObject, 'antragsteller.pass.ausstellenderStaat.valueListItemKey', null),
        passAusgestelltvon: _get(backendObject, 'antragsteller.pass.ausgestelltVon', null),
      },
      reisedaten: {
        // cVisumZweck: _get(decodeResult, 'reisedaten.aufenthaltszweckListe[0].valueListItemKey', null),
        dVisumZweck: _get(backendObject, 'reisedaten.dvisumAufenthaltszweck.valueListItemKey', null),
        dVisumZweckSonstiges: _get(backendObject, 'reisedaten.dvisumAufenthaltszweckSonstiges', null),
        dVisumErwerbstaetigkeit: _get(backendObject, 'reisedaten.dvisumErwerbstaetigkeit', null),
        von: this.stringAsDatepickerModel(_get(backendObject, 'visumdaten.gueltigkeit.von', null)),
        bisGenau: _get(backendObject, 'reisedaten.keine12MonateAufenthalt', false)
          ? this.stringAsDatepickerModel(_get(backendObject, 'visumdaten.gueltigkeit.bisGenau.value', null))
          : null,
        bisUngefaehr: _get(backendObject, 'reisedaten.keine12MonateAufenthalt', false)
          ? null
          : _get(backendObject, 'visumdaten.gueltigkeit.bisUngefaehr.value', null),
        keine12MonateAufenthalt: _get(backendObject, 'reisedaten.keine12MonateAufenthalt', false)
      },

      referenz: this.unmarshallReferenz(backendObject),

      lebensunterhaltAufenthaltsdetails: {
        lebensunterhalt: {
          mittel: _get(backendObject, 'reisedaten.lebensunterhalt.sonstigesText', null),
          abweichenderVerpflichtungsgeber: _get(backendObject, 'referenz.abweichenderVerpflichtungsgeber', null)
            ? 'jaAbweichend'
            : _get(backendObject, 'referenz.foermlicheVerpflichtungserklaerung', null) ? 'ja' : 'nein',
          verpflichtungsgebendeBehoerde: _get(backendObject,
            _get(backendObject, 'verpflichtungserklaerungsgeber.art.valueListItemKey', null)
              ? 'verpflichtungserklaerungsgeber.foermlicheVerpflichtungserklaerung.behoerde.valueListItemKey'
              : 'referenz.foermlicheVerpflichtungserklaerung.behoerde.valueListItemKey', null),
          erklaerungDatum: this.stringAsDatepickerModel(_get(backendObject,
            _get(backendObject, 'verpflichtungserklaerungsgeber.art.valueListItemKey', null)
              ? 'verpflichtungserklaerungsgeber.foermlicheVerpflichtungserklaerung.datum'
              : 'referenz.foermlicheVerpflichtungserklaerung.datum', null)),
          aufenthaltStrasse: _get(backendObject, 'zusatzangaben.aufenthaltStrasse', null),
          aufenthaltHausnummer: _get(backendObject, 'zusatzangaben.aufenthaltHausnummer', null),
          aufenthaltPlz: _get(backendObject, 'zusatzangaben.aufenthaltPlz', null),
          aufenthaltort: _get(backendObject, 'zusatzangaben.aufenthaltOrt', null),
          einzelzimmer: _get(backendObject, 'zusatzangaben.aufenthaltArt.einzelzimmer', null),
          wohnung: _get(backendObject, 'zusatzangaben.aufenthaltArt.wohnung', null),
          zimmer: _get(backendObject, 'zusatzangaben.aufenthaltArt.zimmer', null),
          sammelunterkunft: _get(backendObject, 'zusatzangaben.aufenthaltArt.sammelunterkunft', null),
          sonstiges: _get(backendObject, 'zusatzangaben.aufenthaltArt.sonstiges', null),
          sonstigesTxt: _get(backendObject, 'zusatzangaben.aufenthaltArt.sonstigesText', null),
          wohnortImAusland: _get(backendObject, 'antragsteller.personendaten.wohnortImAusland', null),
          wohnortImAuslandOrt: _get(backendObject, 'antragsteller.personendaten.wohnortImAuslandOrt', null),
          mitreisendeFamilienangehoerige: _get(backendObject, 'antragsteller.personendaten.mitreisendeFamilienangehoerige', null),
          mitreisendeFamilienangehoerigeText: _get(backendObject, 'antragsteller.personendaten.mitreisendeFamilienangehoerigeText', null),
          krankenversicherung: _get(backendObject, 'antragsteller.personendaten.krankenversicherung', null)
        },
        abweichenderVerpflichtungsgeber: {
          art: _get(backendObject, 'verpflichtungserklaerungsgeber.art.valueListItemKey', null),
          familienname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.familienname', null),
          vorname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.vorname', null),
          geschlecht: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.geschlecht.valueListItemKey', null),
          geburtsdatum: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.geburtsdatum', null),
          geburtsort: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.geburtsort', null),
          staatsangehoerigkeit: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.staatsangehoerigkeit.valueListItemKey', null),
          strasse: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.strasse', null),
          hausnummer: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.hausnummer', null),
          plz: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.plz', null),
          ort: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.ort', null),
          land: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.land.valueListItemKey', null),
          telefon: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.kontaktdaten.telefon', null),
          email: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.kontaktdaten.email', null),
          abweichendNachname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.abweichendeSchreibweiseNachname', null),
          abweichendVorname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.abweichendeSchreibweiseVorname', null),
          andereNamen: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.andereNamen', null),
          fruehereNamen: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.fruehereNamen', null),
          orgaName: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.name', null),
          orgaSitz: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.sitz', null),
          orgaSitzLand: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.land.valueListItemKey', null),
          orgaAufgabe: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.wirkungsbereich.valueListItemKey', null),
          orgaRegisterName: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.registerName.valueListItemKey', null),
          orgaRegisterOrt: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.registerOrt', null),
          orgaRegisternummer: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.registerNummer', null)
        },
        fruehereAufenthalte: {
          AufenthaltBRDJa: _get(backendObject, 'fruehereReisen.aufenthalteVorhanden', null),
          aufenthaltOrt1: _get(backendObject, 'fruehereReisen.aufenthaltListe[0].ort', null),
          aufenthaltVon1: _get(backendObject, 'fruehereReisen.aufenthaltListe[0].von', null),
          aufenthaltBis1: _get(backendObject, 'fruehereReisen.aufenthaltListe[0].bis', null),
          aufenthaltOrt2: _get(backendObject, 'fruehereReisen.aufenthaltListe[1].ort', null),
          aufenthaltVon2: _get(backendObject, 'fruehereReisen.aufenthaltListe[1].von', null),
          aufenthaltBis2: _get(backendObject, 'fruehereReisen.aufenthaltListe[1].bis', null),
          aufenthaltOrt3: _get(backendObject, 'fruehereReisen.aufenthaltListe[2].ort', null),
          aufenthaltVon3: _get(backendObject, 'fruehereReisen.aufenthaltListe[2].von', null),
          aufenthaltBis3: _get(backendObject, 'fruehereReisen.aufenthaltListe[2].bis', null),
          aufenthaltOrt4: _get(backendObject, 'fruehereReisen.aufenthaltListe[3].ort', null),
          aufenthaltVon4: _get(backendObject, 'fruehereReisen.aufenthaltListe[3].von', null),
          aufenthaltBis4: _get(backendObject, 'fruehereReisen.aufenthaltListe[3].bis', null),
          aufenthaltOrt5: _get(backendObject, 'fruehereReisen.aufenthaltListe[4].ort', null),
          aufenthaltVon5: _get(backendObject, 'fruehereReisen.aufenthaltListe[4].von', null),
          aufenthaltBis5: _get(backendObject, 'fruehereReisen.aufenthaltListe[4].bis', null)
        },
        erklaerung: {
          vorbestraftJaNein: _get(backendObject, 'erklaerung.vorbestraft', false) === true ? 'ja' : 'nein',
          vorbestraftDeutschlandWann: _get(backendObject, 'erklaerung.strafeInDeutschland.wannUndWo', null),
          vorbestraftDeutschlandGrund: _get(backendObject, 'erklaerung.strafeInDeutschland.grund', null),
          vorbestraftDeutschlandHoehe: _get(backendObject, 'erklaerung.strafeInDeutschland.artUndHoeheDerStrafe', null),
          vorbestraftAuslandWann: _get(backendObject, 'erklaerung.strafeImAusland.wannUndWo', null),
          vorbestraftAuslandGrund: _get(backendObject, 'erklaerung.strafeImAusland.grund', null),
          vorbestraftAuslandHoehe: _get(backendObject, 'erklaerung.strafeImAusland.artUndHoeheDerStrafe', null),
          ausgewiesenJaNein: _get(backendObject, 'erklaerung.ausgewiesen', false) === true ? 'ja' : 'nein',
          ausgewiesenGrund: _get(backendObject, 'erklaerung.ausgewiesenAngaben', null),
          krankJaNein: _get(backendObject, 'erklaerung.krankheiten', false) === true ? 'ja' : 'nein',
          krankInfos: _get(backendObject, 'erklaerung.krankheitenAngaben', null)
        }
      },
      fingerabdruck: {},
      lichtbild: {},
      dokumente: {
        bereitsVorhandeneDokumente: _get(backendObject, 'dokumentListe', null) as VisumDokument
      },
      zusaetzlicheAngaben: this.appConfig.isZusaetzlicheAngabenActive === true ? {
        abweichendeSchreibweiseName: _get(backendObject, 'antragsteller.personendaten.abweichendeSchreibweiseName', null),
        befoerderungsmittel: _get(backendObject, 'reisedaten.befoerderungsmittel.valueListItemKey', null),
        rkvJaNein: _get(backendObject, 'reisedaten.reisekrankenversicherung.daten', null) !== null,
        rkvAnbieter: _get(backendObject, 'reisedaten.reisekrankenversicherung.daten.gesellschaft', null),
        abholer: _get(backendObject, 'sonstiges.abholer', null),
        familienname: _get(backendObject, 'sonstiges.familienname', null),
        vorname: _get(backendObject, 'sonstiges.vorname', null),
        anschrift: _get(backendObject, 'sonstiges.anschrift', null),
        weitereAngaben: _get(backendObject, 'zusatzangaben.weitereAngaben', null)
      } : null,
      kostenuebernahme: {}
    };

    return ret;
  }

  private decodedKinder2Model(decodedKindListe: any[]): any[] {
    if (!decodedKindListe) {
      return [];
    }

    const ret: any[] = [];

    decodedKindListe.forEach((decodedKind) => {
      ret.push({
        familienname: decodedKind.familienname,
        vorname: decodedKind.vorname,
        geschlecht: _get(decodedKind, 'geschlecht.valueListItemKey', null),
        geburtsort: decodedKind.geburtsort,
        geburtsdatum: decodedKind.geburtsdatum,
        wohnort: decodedKind.wohnort,
        staatsangehoerigkeit1: _get(decodedKind, 'staatsangehoerigkeitListe[0].valueListItemKey', null),
        staatsangehoerigkeit2: _get(decodedKind, 'staatsangehoerigkeitListe[1].valueListItemKey', null),
        staatsangehoerigkeit3: _get(decodedKind, 'staatsangehoerigkeitListe[2].valueListItemKey', null)
      });
    });

    return ret;
  }

  marshallKeineReferenzperson(formValue: any): any {
    console.log('Reference "Keine Referenzperson" not supported for the "langfristigen Aufenthalt"');
    return undefined;
  }

  marshallReferenzperson(formValue: any) {
    return {
      referenzArt: this.valueListSelection('referenz_art', formValue.referenz.art),

      verwandtschaftsbeziehungFreitext: this.get(formValue.referenz.einladendePerson_referenzPerson.verwandtschaftsverhaeltnis),

      ansprechpartner: this.getAnsprechpartnerFuerReferenzReferenzperson(formValue),

      foermlicheVerpflichtungserklaerung: formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.abweichenderVerpflichtungsgeber === 'ja'
        ? this.getFoermlicheVerpflichtungserklaerung(formValue)
        : undefined,

      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue)
    };
  }

  marshallOrganisation(formValue: any) {
    return {
      referenzArt: this.valueListSelection('referenz_art', formValue.referenz.art),

      organisation: this.getOrganisationFuerReferenzOrganisation(formValue),

      ansprechpartner: this.getAnsprechpartnerFuerReferenzOrganisation(formValue),

      foermlicheVerpflichtungserklaerung: formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.abweichenderVerpflichtungsgeber === 'ja'
        ? this.getFoermlicheVerpflichtungserklaerung(formValue)
        : undefined,

      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue)
    };
  }

  marshallHotel(formValue: any) {
    console.log('Reference "HOTEL" not supported for the "langfristigen Aufenthalt"');
    return undefined;
  }

  marshallAkkreditierterDiplomat(formValue: any) {
    return {
      referenzArt: this.valueListSelection('referenz_art', formValue.referenz.art),

      organisation: {
        name: this.get(formValue.referenz.akkreditierterDiplomat.orgaName)
      },

      ansprechpartner: this.getAnsprechpartnerFuerReferenzAkkreditierterDiplomat(formValue),

      foermlicheVerpflichtungserklaerung: formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.abweichenderVerpflichtungsgeber === 'ja'
        ? this.getFoermlicheVerpflichtungserklaerung(formValue)
        : undefined,

      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue)
    };
  }

  marshallEuBuerger(formValue: any) {
    console.log('Reference "EU Bürger" not supported for the "langfristigen Aufenthalt"');
    return undefined;
  }

  marshallWohungsinhaber(formValue: any) {
    console.log('Reference "Wohnungsinhaber" not supported for the "langfristigen Aufenthalt"');
    return undefined;
  }

  marshallGewerblicheAnmietung(formValue: any) {
    return {
      referenzArt: this.valueListSelection('referenz_art', formValue.referenz.art),

      organisation: this.getOrganisationFuerReferenzGewerblicheAnmietung(formValue),

      ansprechpartner: this.getAnsprechpartnerFuerReferenzGewerblicheAnmietung(formValue),

      foermlicheVerpflichtungserklaerung: formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.abweichenderVerpflichtungsgeber === 'ja'
        ? this.getFoermlicheVerpflichtungserklaerung(formValue)
        : undefined,

      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue)
    };
  }

  marshallPrivateAnmietung(formValue: any) {
    return {
      referenzArt: this.valueListSelection('referenz_art', formValue.referenz.art),

      ansprechpartner: this.getAnsprechpartnerFuerReferenzPrivateAnmietung(formValue),

      foermlicheVerpflichtungserklaerung: formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.abweichenderVerpflichtungsgeber === 'ja'
        ? this.getFoermlicheVerpflichtungserklaerung(formValue)
        : undefined,

      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue)
    };
  }

  getAbweichenderVerpflichtungsgeber(formValue: any): any {
    return formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.abweichenderVerpflichtungsgeber === 'jaAbweichend';
  }

  protected getFoermlicheVerpflichtungserklaerung(formValue: any): any {
    return {
      behoerde: this.valueListSelection('behoerde', formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.verpflichtungsgebendeBehoerde),
      datum: this.getFormattedDatePickerValue(formValue.lebensunterhaltAufenthaltsdetails.lebensunterhalt.erklaerungDatum)
    };
  }
}
